import React, { useRef, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Paperclip, FileText, Loader2, X, Download, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

/** Build an auth-gated URL an <img>/<a> can load (img tags can't send headers). */
export const attachmentSrc = (url, { download = false } = {}) => {
  if (!url) return '';
  const token = localStorage.getItem('token') || '';
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const sep = url.includes('?') ? '&' : '?';
  return `${base}${url}${sep}auth=${encodeURIComponent(token)}${download ? '&download=true' : ''}`;
};

const MAX_BYTES = 15 * 1024 * 1024;
const ALLOWED = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif',
  'application/pdf', 'text/plain', 'text/csv',
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

const prettySize = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/** Renders a message's attachments (image thumbnails + file cards). */
export const AttachmentList = ({ attachments, align = 'left' }) => {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className={`flex flex-col gap-2 ${align === 'right' ? 'items-end' : 'items-start'}`} data-testid="attachment-list">
      {attachments.map((a, i) => (
        a.kind === 'image' ? (
          <a key={i} href={attachmentSrc(a.url)} target="_blank" rel="noopener noreferrer" data-testid={`attachment-image-${i}`}>
            <img
              src={attachmentSrc(a.url)}
              alt={a.filename || 'attachment'}
              className="max-h-52 max-w-[240px] rounded-lg border border-black/10 object-cover"
              loading="lazy"
            />
          </a>
        ) : (
          <a
            key={i}
            href={attachmentSrc(a.url, { download: true })}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/90 border border-slate-200 rounded-lg px-3 py-2 hover:border-cyan-400 transition-colors max-w-[240px]"
            data-testid={`attachment-file-${i}`}
          >
            <FileText className="w-5 h-5 text-cyan-600 shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-medium text-slate-800 truncate">{a.filename || 'File'}</span>
              <span className="block text-[10px] text-slate-400">{prettySize(a.size)}</span>
            </span>
            <Download className="w-4 h-4 text-slate-400 shrink-0" />
          </a>
        )
      ))}
    </div>
  );
};

/**
 * Attach button + pending-attachment tray. Manages its own upload state.
 * Props:
 *   pending    — array of uploaded attachment objects (controlled by parent)
 *   setPending — setter for pending
 *   disabled   — disable the attach button
 */
export const AttachmentComposer = ({ pending, setPending, disabled }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const file of files) {
      if (!ALLOWED.includes(file.type)) {
        toast.error(`Unsupported file type: ${file.name}`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} is too large (max 15MB)`);
        continue;
      }
      setUploading(true);
      const form = new FormData();
      form.append('file', file);
      try {
        const r = await api.post('/messages/attachments/upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setPending((p) => [...p, r.data]);
      } catch (err) {
        const d = err?.response?.data;
        toast.error(d?.detail || 'Upload failed', {
          description: d?.retryable ? 'Please try again.' : undefined,
          action: d?.retryable ? { label: 'Retry', onClick: () => onFilesRetry(file) } : undefined,
        });
      } finally {
        setUploading(false);
      }
    }
  };

  const onFilesRetry = async (file) => {
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await api.post('/messages/attachments/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPending((p) => [...p, r.data]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed again');
    } finally {
      setUploading(false);
    }
  };

  const remove = (i) => setPending((p) => p.filter((_, idx) => idx !== i));

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt"
        className="hidden"
        onChange={onFiles}
        data-testid="attachment-file-input-picker"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={pick}
        disabled={disabled || uploading}
        className="text-slate-500 hover:text-cyan-600 shrink-0"
        data-testid="attachment-attach-btn"
        aria-label="Attach a photo or file"
      >
        {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
      </Button>
    </>
  );
};

/** Full-width tray of pending (not-yet-sent) attachments. Render above the input. */
export const AttachmentTray = ({ pending, setPending }) => {
  if (!pending || pending.length === 0) return null;
  const remove = (i) => setPending((p) => p.filter((_, idx) => idx !== i));
  return (
    <div className="flex flex-wrap gap-2 mb-2" data-testid="attachment-pending-tray">
      {pending.map((a, i) => (
        <div key={i} className="relative flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-lg pl-2 pr-6 py-1.5" data-testid={`attachment-pending-${i}`}>
          {a.kind === 'image' ? <ImageIcon className="w-4 h-4 text-cyan-600" /> : <FileText className="w-4 h-4 text-cyan-600" />}
          <span className="text-xs text-slate-700 max-w-[120px] truncate">{a.filename}</span>
          <button
            type="button"
            onClick={() => remove(i)}
            className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-rose-600"
            data-testid={`attachment-remove-${i}`}
            aria-label="Remove attachment"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
};
